import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, from, lastValueFrom, of } from 'rxjs';
import { map, tap, switchMap, catchError, filter, take, finalize } from 'rxjs/operators';
import { MsalService } from '@azure/msal-angular';

import { AppConfigService } from '~/core/services/appconfig.service';
import { 
    AuthTokenResponse,
    AuthTokenRequest
} from '~/models/wells.models';

@Injectable({
  providedIn: "root",
})
export class WellsApiService {

  private get config(): any { return this.appConfigService.getConfig(); }
  
  // Token management using BehaviorSubject for reactive updates
  private readonly tokenSubject = new BehaviorSubject<string | null>(
    localStorage.getItem('wells_api_token') ?? null
  );
  public token$ = this.tokenSubject.asObservable();

  // Business Unit selection management
  private readonly BU_KEY = "selectedBU";
  private selectedBUSubject = new BehaviorSubject<string>(
    sessionStorage.getItem(this.BU_KEY) ?? "ABU"
  );
  public selectedBU$ = this.selectedBUSubject.asObservable();

  // Track if we're currently acquiring a token to prevent duplicate calls
  private tokenAcquisitionInProgress = false;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
    private msalService: MsalService
  ) {
    console.log('[WellsApiService] Initialized - Token flow configured');
  }

  //*************************************************************************
  //  Token Acquisition & Management
  //*************************************************************************
  /**
   * Issue a development token using MSAL JWT
   * Acquires MSAL token from MsalService, then exchanges it for Wells API token
   * Ensures MSAL makes fresh call to Azure AD and token endpoint is called once
   */
  public issueDevToken(email: string): Observable<AuthTokenResponse> {
    // If acquisition is already in progress, wait for it to complete
    if (this.tokenAcquisitionInProgress) {
      console.log('[WellsApiService] issueDevToken - Token acquisition already in progress, waiting...');
      return this.token$.pipe(
        filter(token => !!token),
        take(1),
        map(token => ({ token: token!, email: email } as AuthTokenResponse))
      );
    }

    console.log('[WellsApiService] issueDevToken - Starting token acquisition for user:', email);
    
    this.tokenAcquisitionInProgress = true;
    
    // First, acquire MSAL token from MsalService using Observable chain
    return from(this.acquireMsalToken()).pipe(
      tap((msalToken) => {
        console.log('[WellsApiService] issueDevToken - MSAL token received from Azure AD:', !!msalToken);
      }),
      switchMap((msalToken: string | null) => {
        if (!msalToken) {
          throw new Error('Failed to acquire MSAL token. User may not be authenticated.');
        }
        
        console.log('[WellsApiService] issueDevToken - Calling backend endpoint /token/test with MSAL token');
        
        // Now use MSAL token to exchange for Wells API token
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          "Authorization": `Bearer ${msalToken}`
        });

        const body: AuthTokenRequest = { email };

        return this.http.post<AuthTokenResponse>(
          `${this.config.apiUrl}/token/test`, 
          body, 
          { headers }
        );
      }),
      map((response: any) => {
        if (response.token) {
          this.setToken(response.token);
          console.log('[WellsApiService] issueDevToken - Wells API token acquired and stored');
          return response;
        }
        throw new Error("No token received from API");
      }),
      catchError((error) => {
        console.error('[WellsApiService] issueDevToken - Error acquiring token:', error);
        throw error;
      }),
      finalize(() => {
        this.tokenAcquisitionInProgress = false;
      })
    );
  }

  /**
   * Acquire MSAL JWT token from MsalService
   * Uses acquireTokenSilent to get the token without user interaction
   * Forces token refresh to ensure fresh call to Azure AD
   */
  private async acquireMsalToken(): Promise<string | null> {
    try {
      console.log('[WellsApiService] acquireMsalToken - Starting MSAL token acquisition');
      
      // Get the current active account
      const account = this.msalService.instance.getActiveAccount();
      
      console.log('[WellsApiService] acquireMsalToken - Active account:', !!account);
      
      if (!account) {
        console.error('[WellsApiService] acquireMsalToken - No active account found');
        return null;
      }

      console.log('[WellsApiService] acquireMsalToken - Account found, calling acquireTokenSilent with scopes');
      
      // Acquire token silently using Observable (convert with lastValueFrom)
      // Force refresh by setting forceRefresh: true to ensure fresh call to Azure AD
      // Try multiple scope formats: first the clientId/.default, then just clientId
      let tokenResponse: any;
      const clientId = this.config.clientId;
      const scopesToTry = [
        `${clientId}/.default`,        // Try without api:// prefix first
        clientId,                       // Try just client ID
        `https://graph.microsoft.com/.default` // Fallback to Graph API scope for testing
      ];

      for (const scope of scopesToTry) {
        try {
          console.log(`[WellsApiService] acquireMsalToken - Attempting with scope: ${scope}`);
          tokenResponse = await lastValueFrom(
            this.msalService.acquireTokenSilent({
              account: account,
              scopes: [scope],
              forceRefresh: true  // Force fresh token acquisition from Azure AD
            })
          );
          console.log(`[WellsApiService] acquireMsalToken - Successfully acquired token with scope: ${scope}`);
          break; // Success with this scope
        } catch (scopeError: any) {
          console.warn(`[WellsApiService] acquireMsalToken - Failed with scope ${scope}:`, scopeError.message);
          if (scope === scopesToTry[scopesToTry.length - 1]) {
            // Last scope failed, throw the error
            throw scopeError;
          }
          // Continue to next scope
        }
      }

      const msalToken = tokenResponse.accessToken;
      console.log('[WellsApiService] acquireMsalToken - MSAL token acquired successfully from Azure AD, length:', msalToken?.length);
      
      // Store MSAL token for reference
      localStorage.setItem('msal_access_token', msalToken);
      
      return msalToken;
    } catch (error: any) {
      console.error('[WellsApiService] acquireMsalToken - Failed to acquire MSAL token:', error.message, error);
      
      // Don't fallback to cached token - force new acquisition on next attempt
      console.warn('[WellsApiService] acquireMsalToken - Token acquisition failed, will retry on next request');
      return null;
    }
  }

  /**
   * Get cached MSAL JWT from storage
   */
  private getMsalToken(): string | null {
    // Priority: localStorage MSAL token -> config.json fallback
    const token = localStorage.getItem('msal_access_token') || 
                  this.config.access_token || 
                  null;
    
    if (!token) {
      console.warn('[WellsApiService] getMsalToken - No MSAL JWT token available');
    }
    return token;
  }

  /**
   * Store Wells API token in both BehaviorSubject and localStorage
   * Ensures persistence across page refreshes
   */
  public setToken(token: string): void {
    console.log('[WellsApiService] setToken - Storing Wells API token, length:', token?.length);
    this.tokenSubject.next(token);
    localStorage.setItem("wells_api_token", token);
  }

  /**
   * Get current Wells API token from BehaviorSubject, localStorage, or config
   */
  public getToken(): string | null {
    const token =
      this.tokenSubject.value ||
      localStorage.getItem("wells_api_token") ||
      this.config.access_token ||
      null;
    
    console.log('[WellsApiService] getToken - Wells API token available:', !!token);
    return token;
  }

  /**
   * Clear Wells API token from both storage and BehaviorSubject
   */
  public clearToken(): void {
    console.log('[WellsApiService] clearToken - Clearing Wells API token');
    this.tokenSubject.next(null);
    localStorage.removeItem("wells_api_token");
  }

  //*************************************************************************
  //  Business Unit Selection Management
  //*************************************************************************
  /**
   * Set selected Business Unit and persist in sessionStorage
   */
  public setSelectedBU(bu: string): void {
    console.log('[BU] setSelectedBU - BU changed to:', bu);
    sessionStorage.setItem(this.BU_KEY, bu);
    this.selectedBUSubject.next(bu);
  }

  /**
   * Get current selected Business Unit
   */
  public getSelectedBU(): string {
    return this.selectedBUSubject.value;
  }

  /**
   * Clear selected Business Unit selection
   */
  public clearSelectedBU(): void {
    console.log('[BU] clearSelectedBU - Clearing BU selection');
    sessionStorage.removeItem(this.BU_KEY);
    this.selectedBUSubject.next("ABU");
  }

  //*************************************************************************
  //  API Request Helper Methods
  //*************************************************************************
  /**
   * Generate authentication headers with MSAL token (bypasses Wells token exchange)
   * Used for all protected API requests - uses MSAL token directly instead of Wells token
   */
  public getAuthHeaders(): HttpHeaders {
    // Try to get MSAL token from localStorage or config
    const msalToken = this.getMsalToken();
    
    console.log('[WellsApiService] getAuthHeaders - Using MSAL token:', !!msalToken);
    
    if (!msalToken) {
      console.warn('[WellsApiService] getAuthHeaders - No MSAL token available, using basic headers');
      // Return basic headers without auth - request will fail with 401 if auth is required
      return new HttpHeaders({
        "Content-Type": "application/json",
        "Accept": "application/json"
      });
    }
    
    return new HttpHeaders({
      "Content-Type": "application/json",
      "Accept": "application/json",
      "Authorization": `Bearer ${msalToken}`
    });
  }

  /**
   * Check if user has a valid Wells API token
   */
  public hasToken(): boolean {
    return !!this.getToken();
  }

  /**
   * Observable stream of token changes
   * Subscribe to this to react to token updates
   */
  public isAuthenticated$(): Observable<boolean> {
    return this.token$.pipe(
      map(token => !!token)
    );
  }
}